/**
 * In-memory course state (DESIGN.md §3). Units: mm. Origin: top-left.
 */

import type { Course, CourseMetadata, Par, Point, Surfaces, Tee, SurfaceShape, TeePositionColorKey, House } from './types';
import { SURFACE_KEYS, type SurfaceKey, type ElevationGrid, type Pin, type PinDay, type TeePosition, type Marker150 } from './types';
import { pointInPolygon } from './strokeToPolygon';

function createEmptySurfaces(): Surfaces {
  const o = {} as Surfaces;
  for (const k of SURFACE_KEYS) {
    o[k] = [];
  }
  return o;
}

function createElevationGrid(
  widthMm: number,
  heightMm: number,
  cellSizeMm = 5000
): ElevationGrid {
  const cols = Math.max(1, Math.ceil(widthMm / cellSizeMm));
  const rows = Math.max(1, Math.ceil(heightMm / cellSizeMm));
  const count = cols * rows;
  return {
    cols,
    rows,
    cellSizeMm,
    values: new Array<number>(count).fill(0),
  };
}

function nowIso(): string {
  return new Date().toISOString();
}

function defaultCourseName(): string {
  return `Course ${new Date().toISOString().slice(0, 10)}`;
}

const metadataTouchListeners = new Set<() => void>();

export function onMetadataTouched(listener: () => void): () => void {
  metadataTouchListeners.add(listener);
  return () => {
    metadataTouchListeners.delete(listener);
  };
}

/** Full course bounds polygon (origin bottom center: x ±w/2, y 0..h). holeNumber undefined = all holes. */
function defaultSecondCutShape(widthMm: number, heightMm: number): SurfaceShape {
  const halfW = widthMm / 2;
  return {
    type: 'polygon',
    points: [
      [-halfW, 0],
      [halfW, 0],
      [halfW, heightMm],
      [-halfW, heightMm],
    ],
    holeNumber: undefined,
  };
}

export function createCourse(
  widthMm = 100000,
  heightMm = 100000,
  name = defaultCourseName()
): Course {
  const surfaces = createEmptySurfaces();
  surfaces.secondCut = [defaultSecondCutShape(widthMm, heightMm)];
  return {
    version: 1,
    metadata: {
      name,
      holeCount: 18,
      widthMm,
      heightMm,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    },
    surfaces,
    tees: [],
    trees: [],
    houses: [],
    pins: [],
    teePositions: [],
    markers150: [],
    elevation: {
      grid: createElevationGrid(widthMm, heightMm),
    },
    holes: [],
  };
}

const YARD_TO_MM = 914.4;

/** Grid dimensions (yards) per par. Par 3 disables Fairway and 150 yd. */
export const PAR_DIMENSIONS: Record<Par, { widthYd: number; heightYd: number }> = {
  3: { widthYd: 200, heightYd: 300 },
  4: { widthYd: 300, heightYd: 500 },
  5: { widthYd: 400, heightYd: 700 },
};

export function getHolePar(course: Course, holeNumber: number): Par {
  return (course.metadata.holePars?.[holeNumber] as Par) ?? 4;
}

/** Compute course dimensions from hole pars. Uses max width/height across all holes. */
export function getDimensionsFromPars(course: Course): { widthMm: number; heightMm: number } {
  const holeCount = course.metadata.holeCount;
  let maxW = PAR_DIMENSIONS[4].widthYd;
  let maxH = PAR_DIMENSIONS[4].heightYd;
  for (let n = 1; n <= holeCount; n++) {
    const par = getHolePar(course, n);
    const d = PAR_DIMENSIONS[par];
    if (d.widthYd > maxW) maxW = d.widthYd;
    if (d.heightYd > maxH) maxH = d.heightYd;
  }
  return { widthMm: maxW * YARD_TO_MM, heightMm: maxH * YARD_TO_MM };
}

export function setHolePar(course: Course, holeNumber: number, par: Par): void {
  if (!course.metadata.holePars) course.metadata.holePars = {};
  course.metadata.holePars[holeNumber] = par;
  const { widthMm, heightMm } = getDimensionsFromPars(course);
  const prevW = course.metadata.widthMm;
  const prevH = course.metadata.heightMm;
  course.metadata.widthMm = widthMm;
  course.metadata.heightMm = heightMm;
  course.metadata.updatedAt = nowIso();
  // Resize second cut to new bounds
  const secondCut = course.surfaces.secondCut[0];
  if (secondCut) {
    const halfW = widthMm / 2;
    secondCut.points = [
      [-halfW, 0],
      [halfW, 0],
      [halfW, heightMm],
      [-halfW, heightMm],
    ];
  }
  // Recreate elevation grid when dimensions change (note: loses existing elevation data)
  if (widthMm !== prevW || heightMm !== prevH) {
    course.elevation.grid = createElevationGrid(widthMm, heightMm);
  }
  for (const listener of metadataTouchListeners) listener();
}

/** Update course dimensions, second cut, and elevation grid from hole pars. Call when hole count changes. */
export function applyDimensionsFromPars(course: Course): void {
  const { widthMm, heightMm } = getDimensionsFromPars(course);
  const prevW = course.metadata.widthMm;
  const prevH = course.metadata.heightMm;
  course.metadata.widthMm = widthMm;
  course.metadata.heightMm = heightMm;
  course.metadata.updatedAt = nowIso();
  const secondCut = course.surfaces.secondCut[0];
  if (secondCut) {
    const halfW = widthMm / 2;
    secondCut.points = [
      [-halfW, 0],
      [halfW, 0],
      [halfW, heightMm],
      [-halfW, heightMm],
    ];
  }
  if (widthMm !== prevW || heightMm !== prevH) {
    course.elevation.grid = createElevationGrid(widthMm, heightMm);
  }
  for (const listener of metadataTouchListeners) listener();
}

export function touchMetadata(
  course: Course,
  overrides: Partial<CourseMetadata> = {}
): void {
  course.metadata.updatedAt = nowIso();
  if (overrides.name != null) course.metadata.name = overrides.name;
  if (overrides.holeCount != null) course.metadata.holeCount = overrides.holeCount;
  if (overrides.widthMm != null) course.metadata.widthMm = overrides.widthMm;
  if (overrides.heightMm != null) course.metadata.heightMm = overrides.heightMm;
  if (overrides.holePars != null) course.metadata.holePars = overrides.holePars;
  for (const listener of metadataTouchListeners) listener();
}

export function addSurfaceShape(
  course: Course,
  surfaceKey: SurfaceKey,
  shape: { type: 'polygon' | 'path'; points: Point[]; holeNumber?: number }
): void {
  const list = course.surfaces[surfaceKey];
  if (!list) return;
  list.push({
    type: shape.type ?? 'polygon',
    points: shape.points,
    holeNumber: shape.holeNumber ?? 1,
  });
  touchMetadata(course);
}

export function addTee(
  course: Course,
  tee: { points: Point[]; holeNumber?: number; label?: string; id?: string }
): void {
  course.tees.push({
    id: tee.id ?? `tee-${Date.now()}`,
    holeNumber: tee.holeNumber ?? 1,
    label: tee.label ?? '',
    points: tee.points,
  });
  touchMetadata(course);
}

export function addTree(
  course: Course,
  x: number,
  y: number,
  type = '',
  radiusMm = 500,
  holeNumber = 1
): void {
  course.trees.push({ x, y, type, radiusMm, holeNumber });
  touchMetadata(course);
}

export function removeSurfaceShape(
  course: Course,
  surfaceKey: SurfaceKey,
  index: number
): void {
  const list = course.surfaces[surfaceKey];
  if (list && index >= 0 && index < list.length) {
    list.splice(index, 1);
    touchMetadata(course);
  }
}

export function removeTee(course: Course, index: number): void {
  if (index >= 0 && index < course.tees.length) {
    course.tees.splice(index, 1);
    touchMetadata(course);
  }
}

export function removeTree(course: Course, index: number): void {
  if (index >= 0 && index < course.trees.length) {
    course.trees.splice(index, 1);
    touchMetadata(course);
  }
}

export function addHouse(
  course: Course,
  x: number,
  y: number,
  radiusMm = 5000,
  holeNumber = 1
): void {
  course.houses.push({ x, y, radiusMm, holeNumber });
  touchMetadata(course);
}

export function removeHouse(course: Course, index: number): void {
  if (index >= 0 && index < course.houses.length) {
    course.houses.splice(index, 1);
    touchMetadata(course);
  }
}

export function addPin(
  course: Course,
  pin: { x: number; y: number; holeNumber: number; day: PinDay }
): void {
  const holeNumber = pin.holeNumber ?? 1;
  const day = pin.day;
  const existing = course.pins.findIndex((p) => p.holeNumber === holeNumber && p.day === day);
  if (existing >= 0) course.pins.splice(existing, 1);
  course.pins.push({ x: pin.x, y: pin.y, holeNumber, day });
  touchMetadata(course);
}

export function removePin(course: Course, index: number): void {
  if (index >= 0 && index < course.pins.length) {
    course.pins.splice(index, 1);
    touchMetadata(course);
  }
}

/** Remove all content for a hole (surfaces, tees, trees, pins, etc.). Keeps par and name. */
export function clearHoleContent(course: Course, holeNumber: number): void {
  const h = holeNumber;
  const matchHole = (n: number | undefined) => (n ?? 1) !== h;
  course.surfaces.green = course.surfaces.green?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.fairway = course.surfaces.fairway?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.firstCut = course.surfaces.firstCut?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.bunker = course.surfaces.bunker?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.waste = course.surfaces.waste?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.water = course.surfaces.water?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.cartPath = course.surfaces.cartPath?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.outOfBounds = course.surfaces.outOfBounds?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.surfaces.hazard = course.surfaces.hazard?.filter((s) => matchHole(s.holeNumber)) ?? [];
  course.tees = course.tees.filter((t) => matchHole(t.holeNumber));
  course.trees = course.trees.filter((t) => matchHole(t.holeNumber));
  course.houses = course.houses.filter((hx) => matchHole(hx.holeNumber));
  course.pins = course.pins.filter((p) => matchHole(p.holeNumber));
  course.teePositions = course.teePositions.filter((p) => matchHole(p.holeNumber));
  course.markers150 = course.markers150.filter((m) => matchHole(m.holeNumber));
  touchMetadata(course);
}

/** Remove all pins for a hole (e.g. when the green is deleted). */
export function removePinsForHole(course: Course, holeNumber: number): void {
  const before = course.pins.length;
  course.pins = course.pins.filter((p) => (p.holeNumber ?? 1) !== holeNumber);
  if (course.pins.length !== before) touchMetadata(course);
}

export function addTeePosition(
  course: Course,
  pos: { x: number; y: number; holeNumber: number; color?: TeePositionColorKey }
): void {
  const holeNumber = pos.holeNumber ?? 1;
  const color = pos.color ?? 'white';
  const existing = course.teePositions.findIndex(
    (p) => (p.holeNumber ?? 1) === holeNumber && (p.color ?? 'white') === color
  );
  if (existing >= 0) course.teePositions.splice(existing, 1);
  course.teePositions.push({
    x: pos.x,
    y: pos.y,
    holeNumber,
    color: pos.color,
  });
  touchMetadata(course);
}

export function removeTeePosition(course: Course, index: number): void {
  if (index >= 0 && index < course.teePositions.length) {
    course.teePositions.splice(index, 1);
    touchMetadata(course);
  }
}

export function addMarker150(course: Course, marker: { x: number; y: number; holeNumber: number }): void {
  course.markers150.push({ x: marker.x, y: marker.y, holeNumber: marker.holeNumber ?? 1 });
  touchMetadata(course);
}

export function removeMarker150(course: Course, index: number): void {
  if (index >= 0 && index < course.markers150.length) {
    course.markers150.splice(index, 1);
    touchMetadata(course);
  }
}

/** Translate an item by (dxMm, dyMm). */
export function movePin(course: Course, index: number, dxMm: number, dyMm: number): void {
  const p = course.pins[index];
  if (p) {
    p.x += dxMm;
    p.y += dyMm;
    touchMetadata(course);
  }
}

export function moveTeePosition(course: Course, index: number, dxMm: number, dyMm: number): void {
  const p = course.teePositions[index];
  if (p) {
    p.x += dxMm;
    p.y += dyMm;
    touchMetadata(course);
  }
}

export function moveMarker150(course: Course, index: number, dxMm: number, dyMm: number): void {
  const m = course.markers150[index];
  if (m) {
    m.x += dxMm;
    m.y += dyMm;
    touchMetadata(course);
  }
}

export function moveTree(course: Course, index: number, dxMm: number, dyMm: number): void {
  const t = course.trees[index];
  if (t) {
    t.x += dxMm;
    t.y += dyMm;
    touchMetadata(course);
  }
}

export function moveHouse(course: Course, index: number, dxMm: number, dyMm: number): void {
  const h = course.houses[index];
  if (h) {
    h.x += dxMm;
    h.y += dyMm;
    touchMetadata(course);
  }
}

export function moveTee(course: Course, index: number, dxMm: number, dyMm: number): void {
  const tee = course.tees[index];
  if (!tee?.points) return;
  const holeNum = tee.holeNumber ?? 1;
  const teePosIndices: number[] = [];
  for (let i = 0; i < course.teePositions.length; i++) {
    const tp = course.teePositions[i];
    if (!tp || (tp.holeNumber ?? 1) !== holeNum) continue;
    if (pointInPolygon([tp.x, tp.y], tee.points)) teePosIndices.push(i);
  }
  for (const p of tee.points) {
    p[0] += dxMm;
    p[1] += dyMm;
  }
  for (const i of teePosIndices) {
    const tp = course.teePositions[i];
    if (tp) {
      tp.x += dxMm;
      tp.y += dyMm;
    }
  }
  touchMetadata(course);
}

export function moveSurfaceShape(
  course: Course,
  surfaceKey: SurfaceKey,
  index: number,
  dxMm: number,
  dyMm: number
): void {
  const list = course.surfaces[surfaceKey];
  const shape = list?.[index];
  if (!shape?.points) return;
  const holeNum = shape.holeNumber ?? 1;
  const pinIndices: number[] = [];
  if (surfaceKey === 'green') {
    for (let i = 0; i < course.pins.length; i++) {
      const pin = course.pins[i];
      if (!pin || (pin.holeNumber ?? 1) !== holeNum) continue;
      if (pointInPolygon([pin.x, pin.y], shape.points)) pinIndices.push(i);
    }
  }
  for (const p of shape.points) {
    p[0] += dxMm;
    p[1] += dyMm;
  }
  if (surfaceKey === 'green') {
    for (const i of pinIndices) {
      const pin = course.pins[i];
      if (pin) {
        pin.x += dxMm;
        pin.y += dyMm;
      }
    }
  }
  touchMetadata(course);
}

/** Move item to index 0 (back of draw order). Returns new index (0). */
export function sendSurfaceShapeToBack(course: Course, surfaceKey: SurfaceKey, index: number): number {
  const list = course.surfaces[surfaceKey];
  if (!list || index < 0 || index >= list.length) return index;
  const [item] = list.splice(index, 1);
  if (item) list.unshift(item);
  touchMetadata(course);
  return 0;
}

export function sendTeeToBack(course: Course, index: number): number {
  if (index < 0 || index >= course.tees.length) return index;
  const [item] = course.tees.splice(index, 1);
  if (item) course.tees.unshift(item);
  touchMetadata(course);
  return 0;
}

export function sendTreeToBack(course: Course, index: number): number {
  if (index < 0 || index >= course.trees.length) return index;
  const [item] = course.trees.splice(index, 1);
  if (item) course.trees.unshift(item);
  touchMetadata(course);
  return 0;
}

export function sendHouseToBack(course: Course, index: number): number {
  if (index < 0 || index >= course.houses.length) return index;
  const [item] = course.houses.splice(index, 1);
  if (item) course.houses.unshift(item);
  touchMetadata(course);
  return 0;
}

export function sendPinToBack(course: Course, index: number): number {
  if (index < 0 || index >= course.pins.length) return index;
  const [item] = course.pins.splice(index, 1);
  if (item) course.pins.unshift(item);
  touchMetadata(course);
  return 0;
}

export function sendTeePositionToBack(course: Course, index: number): number {
  if (index < 0 || index >= course.teePositions.length) return index;
  const [item] = course.teePositions.splice(index, 1);
  if (item) course.teePositions.unshift(item);
  touchMetadata(course);
  return 0;
}

export function sendMarker150ToBack(course: Course, index: number): number {
  if (index < 0 || index >= course.markers150.length) return index;
  const [item] = course.markers150.splice(index, 1);
  if (item) course.markers150.unshift(item);
  touchMetadata(course);
  return 0;
}

/** xMm, yMm are in bottom-center coords (origin at bottom center, y up). */
export function setElevationInRegion(
  course: Course,
  xMm: number,
  yMm: number,
  radiusMm: number,
  value: number
): void {
  const g = course.elevation?.grid;
  if (!g?.values) return;
  const { cols, rows, cellSizeMm } = g;
  const widthMm = course.metadata.widthMm;
  const heightMm = course.metadata.heightMm;
  const halfW = widthMm / 2;
  const left = Math.max(0, Math.floor((xMm - radiusMm + halfW) / cellSizeMm));
  const right = Math.min(cols - 1, Math.ceil((xMm + radiusMm + halfW) / cellSizeMm));
  const topY = heightMm - yMm - radiusMm;
  const bottomY = heightMm - yMm + radiusMm;
  const rowMin = Math.max(0, Math.floor(topY / cellSizeMm));
  const rowMax = Math.min(rows - 1, Math.floor(bottomY / cellSizeMm));
  for (let row = rowMin; row <= rowMax; row++) {
    for (let col = left; col <= right; col++) {
      const cx = (col + 0.5) * cellSizeMm - halfW;
      const cy = heightMm - (row + 0.5) * cellSizeMm;
      const d = Math.hypot(cx - xMm, cy - yMm);
      if (d <= radiusMm) {
        const i = row * cols + col;
        g.values[i] = Math.max(0, Math.min(1, value));
      }
    }
  }
  touchMetadata(course);
}

/** Smooth falloff: 1 at center, 0 at radius (quadratic). Gradual blend at edges. */
function elevationWeightSmooth(d: number, radiusMm: number): number {
  if (d >= radiusMm) return 0;
  const t = d / radiusMm;
  return 1 - t * t;
}

/** Steep falloff: stays near 1 in center, drops sharply near edge (quartic). */
function elevationWeightSteep(d: number, radiusMm: number): number {
  if (d >= radiusMm) return 0;
  const t = d / radiusMm;
  return 1 - t * t * t * t;
}

/** xMm, yMm in bottom-center coords. Adds delta (normalized 0–1) in a radius.
 *  edgeMode: 'smooth' = gradual blend, 'steep' = sharp drop-off at edges. */
export function addElevationInRegion(
  course: Course,
  xMm: number,
  yMm: number,
  radiusMm: number,
  delta: number,
  edgeMode: 'smooth' | 'steep' = 'smooth'
): void {
  const g = course.elevation?.grid;
  if (!g?.values) return;
  const { cols, rows, cellSizeMm } = g;
  const widthMm = course.metadata.widthMm;
  const heightMm = course.metadata.heightMm;
  const halfW = widthMm / 2;
  const left = Math.max(0, Math.floor((xMm - radiusMm + halfW) / cellSizeMm));
  const right = Math.min(cols - 1, Math.ceil((xMm + radiusMm + halfW) / cellSizeMm));
  const topY = heightMm - yMm - radiusMm;
  const bottomY = heightMm - yMm + radiusMm;
  const rowMin = Math.max(0, Math.floor(topY / cellSizeMm));
  const rowMax = Math.min(rows - 1, Math.floor(bottomY / cellSizeMm));
  for (let row = rowMin; row <= rowMax; row++) {
    for (let col = left; col <= right; col++) {
      const cx = (col + 0.5) * cellSizeMm - halfW;
      const cy = heightMm - (row + 0.5) * cellSizeMm;
      const d = Math.hypot(cx - xMm, cy - yMm);
      const w = edgeMode === 'steep' ? elevationWeightSteep(d, radiusMm) : elevationWeightSmooth(d, radiusMm);
      if (w > 0) {
        const i = row * cols + col;
        g.values[i] = Math.max(0, Math.min(1, (g.values[i] ?? 0) + delta * w));
      }
    }
  }
  touchMetadata(course);
}

/** Add delta (normalized) to all grid cells whose center is inside the polygon. Points in bottom-center coords. */
export function addElevationInPolygon(course: Course, points: Point[], delta: number): void {
  if (points.length < 3) return;
  const g = course.elevation?.grid;
  if (!g?.values) return;
  const { cols, rows, cellSizeMm } = g;
  const widthMm = course.metadata.widthMm;
  const heightMm = course.metadata.heightMm;
  const halfW = widthMm / 2;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const cx = (col + 0.5) * cellSizeMm - halfW;
      const cy = heightMm - (row + 0.5) * cellSizeMm;
      if (pointInPolygon([cx, cy], points)) {
        const i = row * cols + col;
        g.values[i] = Math.max(0, Math.min(1, (g.values[i] ?? 0) + delta));
      }
    }
  }
  touchMetadata(course);
}

export function toJSON(course: Course): Course {
  const copy = JSON.parse(JSON.stringify(course)) as Course;
  if (copy.elevation?.grid?.values) {
    copy.elevation.grid.values = [...copy.elevation.grid.values];
  }
  return copy;
}

export function fromJSON(obj: unknown): Course {
  const o = obj as Record<string, unknown>;
  const meta = (o.metadata as Record<string, unknown>) ?? {};
  const widthMm = (meta.widthMm as number) ?? 100000;
  const heightMm = (meta.heightMm as number) ?? 100000;

  const course: Course = {
    version: (o.version as number) ?? 1,
    metadata: {
      name: (meta.name as string) ?? 'Untitled',
      holeCount: (meta.holeCount as 9 | 18) === 9 ? 9 : 18,
      widthMm,
      heightMm,
      createdAt: (meta.createdAt as string) ?? nowIso(),
      updatedAt: (meta.updatedAt as string) ?? nowIso(),
      holeNames: (meta.holeNames as Record<number, string>) ?? undefined,
      holePars: (meta.holePars as Record<number, Par>) ?? undefined,
    },
    surfaces: { ...createEmptySurfaces(), ...(o.surfaces as Partial<Surfaces>) },
    tees: Array.isArray(o.tees) ? (o.tees as Tee[]) : [],
    trees: Array.isArray(o.trees) ? (o.trees as Course['trees']) : [],
    houses: Array.isArray(o.houses) ? (o.houses as House[]) : [],
    pins: Array.isArray(o.pins)
      ? (o.pins as Array<Pin & { day?: PinDay }>).map((p) => ({ ...p, day: p.day ?? 'thu' }))
      : [],
    teePositions: Array.isArray(o.teePositions) ? (o.teePositions as TeePosition[]) : [],
    markers150: Array.isArray(o.markers150) ? (o.markers150 as Marker150[]) : [],
    elevation:
      (o.elevation as Course['elevation']) ?? {
        grid: createElevationGrid(widthMm, heightMm),
      },
    holes: Array.isArray(o.holes) ? (o.holes as Course['holes']) : [],
  };

  for (const k of SURFACE_KEYS) {
    if (!Array.isArray(course.surfaces[k])) course.surfaces[k] = [];
    for (const shape of course.surfaces[k]) {
      if (shape && shape.holeNumber == null && k !== 'secondCut') shape.holeNumber = 1;
    }
  }
  if (course.surfaces.secondCut.length === 0) {
    course.surfaces.secondCut = [defaultSecondCutShape(course.metadata.widthMm, course.metadata.heightMm)];
  }
  for (const tree of course.trees) {
    if (tree && tree.holeNumber == null) tree.holeNumber = 1;
  }
  for (const house of course.houses) {
    if (house && house.holeNumber == null) house.holeNumber = 1;
  }
  if (!course.elevation.grid?.values) {
    course.elevation.grid = createElevationGrid(
      course.metadata.widthMm,
      course.metadata.heightMm
    );
  }
  return course;
}
